import React, { useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './index.css';

const Calendar = ({ villa, selectedDates, setSelectedDates, onClose, onBookNow }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const MINIMUM_NIGHTS = 2;
  const today = new Date();
  today.setHours(0, 0, 0, 0); 

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const isDateBlocked = (date) => {
    const formattedDate = date.toISOString().split('T')[0];
    return (villa.blockedDates || []).includes(formattedDate);
  };

  const isDateBeforeToday = (date) => {
    return date < today;
  };

  const isDateSelected = (date) => {
    if (selectedDates.length === 0) return false;
    if (selectedDates.length === 1) {
      return date.toDateString() === selectedDates[0].toDateString();
    }
    return date >= selectedDates[0] && date <= selectedDates[1];
  };

  const isInRange = (date) => {
    if (selectedDates.length !== 2) return false;
    return date > selectedDates[0] && date < selectedDates[1];
  };

  const isValidMinStay = (checkIn, checkOut) => {
    const diffTime = Math.abs(checkOut - checkIn);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= MINIMUM_NIGHTS;
  };

  const handleDateClick = (date) => {
    if (isDateBlocked(date) || isDateBeforeToday(date)) return;

    if (selectedDates.length === 0 || selectedDates.length === 2) {
      setSelectedDates([date]);
    } else if (selectedDates.length === 1) {
      const [firstDate] = selectedDates;
      let checkIn, checkOut;

      if (date < firstDate) {
        checkIn = date;
        checkOut = firstDate;
      } else {
        checkIn = firstDate;
        checkOut = date;
      }
      let currentDate = new Date(checkIn);
      while (currentDate <= checkOut) {
        if (isDateBlocked(currentDate)) {
          toast.error('Selected range includes blocked dates.');
          return;
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }

      if (isValidMinStay(checkIn, checkOut)) {
        setSelectedDates([checkIn, checkOut]);
      } else {
        toast.error(`Please select a stay of at least ${MINIMUM_NIGHTS} nights.`);
      }
    }
  };

  const changeMonth = (delta) => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + delta, 1);
    // Prevent navigating to months before the current month
    if (newMonth < new Date(today.getFullYear(), today.getMonth(), 1)) {
      return;
    }
    setCurrentMonth(newMonth);
  };

  const handleBookNow = () => {
    if (selectedDates.length === 2 && isValidMinStay(selectedDates[0], selectedDates[1])) {
      console.log('Calendar: Book Now clicked, passing to MyVillas:', { villa, selectedDates });
      onBookNow(villa, selectedDates);
    } else {
      toast.error(`Please select a stay of at least ${MINIMUM_NIGHTS} nights.`);
    }
  };

  const renderMonth = (offset) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + offset);
    const daysInMonth = getDaysInMonth(date);
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      const currentDate = new Date(date.getFullYear(), date.getMonth(), i);
      const isDisabled = isDateBlocked(currentDate) || isDateBeforeToday(currentDate);
      const isSelected = isDateSelected(currentDate);
      const isRange = isInRange(currentDate);
      
      days.push(
        <div
          key={i}
          className={`calendar-day ${
            isDisabled ? 'disabled' : ''
          } ${isSelected ? 'selected' : ''} ${isRange ? 'in-range' : ''}`}
          onClick={() => !isDisabled && handleDateClick(currentDate)}
        >
          {i}
        </div>
      );
    }

    return (
      <div className="calendar-month">
        <div className="calendar-header">
          {date.toLocaleString('default', { month: 'long', year: 'numeric' })}
        </div>
        <div className="calendar-grid">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="calendar-day-header">{day}</div>
          ))}
          {days}
        </div>
      </div>
    );
  };

  return (
    <div className="calendar-overlay-booking">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover />
      <div className="calendar-container-booking">
        <div className="calendar-header-controls">
          <h3>{villa.title} Availability</h3>
          <div className="calendar-navigation">
            <button
              className="btn btn-nav"
              onClick={() => changeMonth(-2)}
              disabled={new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 2, 1) < new Date(today.getFullYear(), today.getMonth(), 1)}
            >
              ←
            </button>
            <button className="btn btn-nav" onClick={() => changeMonth(2)}>
              →
            </button>
          </div>
        </div>
        <div className="calendar-content">
          {renderMonth(0)}
          {renderMonth(1)}
        </div>
        <div className="calendar-actions">
          <button className="btn btn-close-calendar" onClick={onClose}>
            Close
          </button>
          <button
            className="btn btn-book-now"
            onClick={handleBookNow}
            disabled={selectedDates.length !== 2}
          >
            Book Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default Calendar;